import React from 'react';
import { Menu, User, LogOut, Settings, Bell, Baby, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useSectionContext } from '@/contexts/SectionContext';
import { romanianLabels } from '@/utils/romanianLabels';

interface HeaderProps {
  onMenuClick: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { utilizator, logout } = useAuth();
  const { activeSection } = useSectionContext();

  // GET /api/auth/profile (refreshed on every mount/reload) returns the JWT's
  // embedded user claim, which has first_name/last_name but no computed
  // `name` - unlike /auth/login's response. Fall back to building it so the
  // header doesn't regress to "Utilizator" after a reload.
  const displayName =
    utilizator?.name ||
    [utilizator?.first_name, utilizator?.last_name].filter(Boolean).join(' ') ||
    'Utilizator';

  const handleNavigation = (page: string) => {
    const event = new CustomEvent('navigate', { detail: { page } });
    window.dispatchEvent(event);
  };

  const getUserLevelLabel = () => {
    if (!utilizator) return '';
    const levelId = utilizator.user_level?.id || utilizator.user_level_id;
    switch (levelId) {
      case 3:
        return 'Administrator';
      case 2:
        return 'Utilizator spital';
      case 1:
        return 'Utilizator maternitate';
      default:
        return 'Utilizator';
    }
  };

  // Get user level
  const userLevel = utilizator?.user_level?.id || utilizator?.user_level_id || 0;
  const isHospitalUser = userLevel === 2;

  // Get section badge info
  const getSectionBadge = () => {
    if (!isHospitalUser || !activeSection) return null;

    if (activeSection === 'prematuri') {
      return {
        icon: Baby,
        label: 'Prematuri',
        className: 'bg-purple-500 hover:bg-purple-600',
      };
    } else if (activeSection === 'chd') {
      return {
        icon: Heart,
        label: 'MCC',
        className: 'bg-red-500 hover:bg-red-600',
      };
    }
    return null;
  };

  const sectionBadge = getSectionBadge();

  return (
    <header className="sticky top-0 z-50 h-16 border-b bg-card shadow-sm">
      <div className="flex h-full items-center justify-between px-3 sm:px-4">
        {/* Left Section */}
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex items-center gap-2">
            <h1 className="text-lg sm:text-xl font-bold text-primary">
              Registru
            </h1>
            <Badge variant="secondary" className="text-xs">v1.0.0</Badge>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Section Badge for Hospital Users */}
          {sectionBadge && (
            <Badge variant="outline" className="flex items-center gap-1.5 px-2.5 py-1">
              <sectionBadge.icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline text-xs">{sectionBadge.label}</span>
            </Badge>
          )}

          {/* Notifications */}
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2">
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <User className="h-3 w-3 sm:h-4 sm:w-4" />
                </div>
                <div className="hidden text-left sm:block">
                  <p className="text-sm font-medium">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{getUserLevelLabel()}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel>Contul meu</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleNavigation('profile')}>
                <User className="mr-2 h-4 w-4" />
                <span>Profil</span>
              </DropdownMenuItem>
              {(utilizator?.user_level?.id === 3 || utilizator?.user_level_id === 3) && (
                <DropdownMenuItem onClick={() => handleNavigation('settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Setări</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                <span>{romanianLabels.navigation.logout}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
